// recipeDetailsService.js - Services for recipe details and image generation
import { MODEL_CONFIG, MODEL_REASONING_CONFIG, getProviderForModel, supportsOpenAIReasoning, cleanJsonResponse, generationCostTracker } from './apiConfig';
import { streamFromBackend, fetchFromBackend } from './aiProxyClient';

/**
 * Convert cooking instructions to a readable format for image generation.
 * Handles current format: [{step_title, details: [string]}]
 * 
 * @param {Array} cookingInstructions - Array of cooking instruction steps
 * @returns {String} Formatted instructions for image prompt
 */
const formatInstructionsForImagePrompt = (cookingInstructions) => {
  if (!cookingInstructions || !Array.isArray(cookingInstructions)) {
    return '';
  }

  return cookingInstructions
    .map(step => {
      const title = step.step_title || step.title || '';
      const details = Array.isArray(step.details)
        ? step.details.map(d => typeof d === 'string' ? d : d?.detail || '').join(' ')
        : '';
      return title ? `Step: ${title}. ${details}` : details;
    })
    .filter(Boolean)
    .join(' ')
    .trim();
};

/**
 * Safely extract a completed JSON string field value from a streaming buffer.
 * Detects closing quote accounting for escape sequences.
 * Returns {complete: false} if the value is not a string (e.g. object, array).
 */
export const extractJsonStringField = (text, fieldName) => {
  try {
    const key = `"${fieldName}"`;
    const keyIdx = text.indexOf(key);
    if (keyIdx === -1) return { complete: false };

    // Find the colon after the key
    let colonIdx = text.indexOf(':', keyIdx + key.length);
    if (colonIdx === -1) return { complete: false };

    // Skip whitespace after colon
    let valueStart = colonIdx + 1;
    while (valueStart < text.length && /\s/.test(text[valueStart])) valueStart++;

    if (valueStart >= text.length) return { complete: false };

    // Check that the value is a string (starts with "). If it's { or [ or other, bail out.
    if (text[valueStart] !== '"') return { complete: false };

    // Scan to find closing unescaped quote
    let i = valueStart + 1;
    while (i < text.length) {
      if (text[i] === '"') {
        // Count preceding backslashes
        let bs = 0, j = i - 1;
        while (j >= 0 && text[j] === '\\') { bs++; j--; }
        if ((bs % 2) === 0) {
          const raw = text.slice(valueStart + 1, i);
          // Decode JSON string escapes by leveraging JSON.parse
          let decoded = raw;
          try { decoded = JSON.parse(`"${raw}"`); } catch {}
          return { complete: true, value: decoded };
        }
      }
      i++;
    }

    return { complete: false };
  } catch {
    return { complete: false };
  }
};

/**
 * Extract a completed JSON object field value from a streaming buffer.
 * Uses brace-counting with string awareness to find the closing }.
 * Returns {complete: true, value: parsedObject} or {complete: false}.
 */
export const extractJsonObjectField = (text, fieldName) => {
  try {
    const key = `"${fieldName}"`;
    const keyIdx = text.indexOf(key);
    if (keyIdx === -1) return { complete: false };

    let colonIdx = text.indexOf(':', keyIdx + key.length);
    if (colonIdx === -1) return { complete: false };

    // Skip whitespace after colon
    let valueStart = colonIdx + 1;
    while (valueStart < text.length && /\s/.test(text[valueStart])) valueStart++;

    if (valueStart >= text.length || text[valueStart] !== '{') return { complete: false };

    // Brace-counting with string awareness
    let depth = 0;
    let inString = false;
    let escape = false;
    for (let i = valueStart; i < text.length; i++) {
      const ch = text[i];
      if (escape) { escape = false; continue; }
      if (ch === '\\') { escape = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (!inString) {
        if (ch === '{') depth++;
        else if (ch === '}') {
          depth--;
          if (depth === 0) {
            const raw = text.slice(valueStart, i + 1);
            try {
              return { complete: true, value: JSON.parse(raw) };
            } catch {
              return { complete: false };
            }
          }
        }
      }
    }

    return { complete: false };
  } catch {
    return { complete: false };
  }
};

// Extract the first balanced top-level JSON object from text (ignores braces inside strings)
const extractFirstBalancedJsonObject = (text) => {
  if (!text) return null;
  const start = text.indexOf('{');
  if (start === -1) return null;
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = start; i < text.length; i++) {
    const ch = text[i];
    if (escape) { escape = false; continue; }
    if (ch === '\\') { escape = true; continue; }
    if (ch === '"') { inString = !inString; continue; }
    if (!inString) {
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) return text.slice(start, i + 1);
      }
    }
  }
  return null;
};

// Attempt to repair common JSON issues (trailing commas, missing commas, smart quotes, NBSP)
const repairJson = (text) => {
  if (!text) return text;
  let t = String(text);
  // Normalize smart quotes and NBSP
  t = t.replace(/[\u201C\u201D]/g, '"').replace(/[\u2018\u2019]/g, "'").replace(/\u00A0/g, ' ');
  // Remove markdown fences if any linger
  if (t.startsWith('```json')) t = t.slice(7);
  if (t.startsWith('```')) t = t.slice(3);
  if (t.endsWith('```')) t = t.slice(0, -3);
  // Trim outside noise to first balanced object if possible
  const balanced = extractFirstBalancedJsonObject(t);
  if (balanced) t = balanced;
  // Remove trailing commas before closing } or ]
  t = t.replace(/,\s*(\}|\])/g, '$1');
  // Insert missing commas between JSON elements (common LLM error)
  t = insertMissingCommas(t);
  return t.trim();
};

/**
 * Insert missing commas between JSON elements.
 * LLMs sometimes omit commas between array elements or object properties.
 */
const insertMissingCommas = (text) => {
  let t = text;
  // Between adjacent objects: }  {
  t = t.replace(/\}(\s+)\{/g, '},$1{');
  // Between end of object and next key: }  "key"
  t = t.replace(/\}(\s+)"/g, '},$1"');
  // Between end of array and next key: ]  "key"
  t = t.replace(/\](\s+)"/g, '],$1"');
  // Between end of array and next object: ]  {
  t = t.replace(/\](\s+)\{/g, '],$1{');
  // Between number/boolean/null and next key: 42  "key" or true  "key"
  t = t.replace(/(\d)(\s+)"/g, '$1,$2"');
  t = t.replace(/(true|false|null)(\s+)"/g, '$1,$2"');
  // Between string value and next object key: "value"  "key":
  // (second string IS followed by colon — it's a property key)
  t = t.replace(/"(\s+)"((?:[^"\\]|\\.)*)"(\s*:)/g, '",$1"$2"$3');
  // Between adjacent string values in arrays: "foo"  "bar"
  // (second string is NOT followed by colon — it's an array element)
  // Loop to handle 3+ consecutive elements without commas
  let prev;
  do {
    prev = t;
    t = t.replace(/"(\s+)"((?:[^"\\]|\\.)*)"(?!\s*:)/g, '",$1"$2"');
  } while (t !== prev);
  return t;
};

/**
 * Last-resort: try to fix JSON by inserting commas at positions where JSON.parse reports errors.
 * Attempts up to 10 fixes before giving up.
 */
const fixJsonAtErrorPositions = (text) => {
  let t = text;
  for (let attempt = 0; attempt < 10; attempt++) {
    try {
      return JSON.parse(t);
    } catch (e) {
      const posMatch = e.message.match(/position\s+(\d+)/);
      if (!posMatch) return null;
      const errorPos = parseInt(posMatch[1]);
      if (errorPos <= 0 || errorPos >= t.length) return null;
      // Walk backwards from error position past whitespace to find insertion point
      let insertAt = errorPos;
      let check = errorPos - 1;
      while (check >= 0 && /\s/.test(t[check])) check--;
      if (check >= 0 && /["\d}\]esl]/.test(t[check])) {
        // Looks like end of a value — insert comma after it
        insertAt = check + 1;
        t = t.slice(0, insertAt) + ',' + t.slice(insertAt);
      } else {
        return null;
      }
    }
  }
  return null;
};

/**
 * Extract a balanced JSON array for a given field name from text.
 * Handles nested objects/arrays and string escaping.
 * Returns parsed array on success, null on failure.
 */
const extractJsonArrayField = (text, fieldName) => {
  try {
    const regex = new RegExp(`"${fieldName}"\\s*:\\s*\\[`);
    const match = text.match(regex);
    if (!match) return null;

    const startIdx = match.index + match[0].length - 1;
    let depth = 0;
    let inString = false;
    let escape = false;

    for (let i = startIdx; i < text.length; i++) {
      const ch = text[i];
      if (escape) { escape = false; continue; }
      if (ch === '\\') { escape = true; continue; }
      if (ch === '"') { inString = !inString; continue; }
      if (!inString) {
        if (ch === '[') depth++;
        else if (ch === ']') {
          depth--;
          if (depth === 0) {
            const arrayStr = text.slice(startIdx, i + 1);
            try {
              return JSON.parse(arrayStr);
            } catch {
              // Try to repair and parse partial array
              let repaired = arrayStr.replace(/,\s*$/, '').replace(/,\s*\]/, ']');
              if (!repaired.endsWith(']')) {
                repaired = repaired.replace(/,\s*$/, '') + ']';
              }
              try { return JSON.parse(repaired); } catch {}
            }
            break;
          }
        }
      }
    }
  } catch {}
  return null;
};

/**
 * Attempt to extract partial recipe data from a truncated/incomplete JSON response.
 * This is a last-resort fallback when JSON.parse fails completely.
 */
const extractPartialRecipeData = (text) => {
  const result = {};
  
  // Extract string fields
  const title = extractJsonStringField(text, 'updated_title');
  if (title.complete && title.value) result.updated_title = title.value;

  const vis = extractJsonStringField(text, 'visual_description');
  if (vis.complete && vis.value) result.visual_description = vis.value;
  
  const desc = extractJsonStringField(text, 'full_description');
  if (desc.complete && desc.value) result.full_description = desc.value;

  const cookTime = extractJsonStringField(text, 'cooking_time');
  if (cookTime.complete && cookTime.value) result.cooking_time = cookTime.value;

  const storageTime = extractJsonStringField(text, 'max_storage_time');
  if (storageTime.complete && storageTime.value) result.max_storage_time = storageTime.value;
  
  // Extract array fields
  const arrays = ['ingredient_storage', 'cooking_instructions', 'tips', 'equipment'];
  for (const field of arrays) {
    const arr = extractJsonArrayField(text, field);
    if (arr) result[field] = arr;
  }
  
  return Object.keys(result).length > 0 ? result : null;
};

/**
 * Fetch detailed recipe instructions with streaming support.
 * In the new flow, images are already generated from o3-mini's description,
 * so visual_description extraction is no longer needed for early image generation.
 * Instead, this function now accepts matched grocery ingredients to generate
 * accurate recipe details based on real available products.
 * 
 * @param {String} recipePrompt - Prompt for recipe details
 * @param {Function} onChunk - Callback for progressive text buffer
 * @param {Function} onReasoning - Callback for reasoning steps
 * @param {Object} options - Optional settings: { title, description, onImage, autoGenerateImage }
 * @returns {Promise<Object|String>} Recipe details JSON object or raw buffer if parsing fails
 */
export const fetchRecipeDetails = async (recipePrompt, onChunk, onReasoning, options = {}) => {
  let buffer = '';
  const { title } = options;
  
  // Timing tracking
  const startTime = Date.now();
  const safeTitle = title || 'Meal';
  
  console.log(`[${safeTitle}] 🚀 Starting recipe details fetch at ${new Date().toISOString()}`);
  
  const systemPrompt = `You are an experienced culinary expert providing detailed cooking instructions strictly in JSON format.

You will receive a recipe with its ACTUAL available ingredients from the grocery store. Some ingredients may differ from the original recipe concept (e.g., ground oregano instead of fresh oregano, frozen spinach instead of fresh). Your instructions MUST account for these real ingredients — adjust cooking times, preparation steps, and techniques accordingly.`;

  let chunkCount = 0;

  const detailsModel = MODEL_CONFIG.details;
  const detailsProvider = getProviderForModel(detailsModel);
  const maxTokens = 5000;
  const promptCharLength = typeof recipePrompt === 'string' ? recipePrompt.length : 0;
  console.log(`[${safeTitle}] Using ${detailsProvider} (${detailsModel}) for recipe details, prompt=${promptCharLength} chars, maxTokens=${maxTokens}`);

  const requestBody = {
    model: detailsModel,
    systemPrompt,
    userPrompt: recipePrompt,
    maxTokens,
  };

  // Cap reasoning effort — mirrors the completion path in mealPlanService.js.
  if (detailsProvider === 'openai' && supportsOpenAIReasoning(detailsModel)) {
    const reasoningConfig = MODEL_REASONING_CONFIG.details || {};
    requestBody.reasoning = { effort: reasoningConfig.effort || 'low' };
    if (reasoningConfig.maxTokens) {
      requestBody.maxTokens = Math.max(requestBody.maxTokens, reasoningConfig.maxTokens);
    }
  }

  // Stream recipe details via backend proxy (provider-agnostic)
  await streamFromBackend('/recipe-details', requestBody, {
    onText: (deltaText) => {
      if (!deltaText) return;
      chunkCount++;
      buffer += deltaText;
      if (onChunk) onChunk(buffer);
    },
    onReasoning: (text) => {
      if (text && onReasoning) onReasoning(text);
    },
    onUsage: (usage) => {
      if (usage?.input_tokens && usage?.output_tokens) {
        const cached = usage.input_tokens_details?.cached_tokens || usage.cache_read_input_tokens || 0;
        generationCostTracker.logRecipeDetails(safeTitle, detailsModel, usage.input_tokens, usage.output_tokens, cached);
      }
    },
    onFinalText: (definitiveText) => {
      if (definitiveText) {
        buffer = definitiveText;
        console.log(`[${safeTitle}] 📄 Using authoritative text from finalResponse (${definitiveText.length} chars)`);
      }
    },
    onError: (err) => {
      console.error(`[${safeTitle}] Stream error:`, err);
    },
  });

  const streamTime = ((Date.now() - startTime) / 1000).toFixed(2);
  console.log(`[${safeTitle}] ✓ Recipe details stream complete in ${streamTime}s (${chunkCount} chunks, response=${buffer.length} chars)`);

  const parseStartTime = Date.now();
  let candidate = cleanJsonResponse(buffer);
  try {
    // Try direct clean parse first
    try {
      if (candidate && candidate.trim()) {
        const obj = JSON.parse(candidate);
        const parseTime = ((Date.now() - parseStartTime) / 1000).toFixed(3);
        const totalTime = ((Date.now() - startTime) / 1000).toFixed(2);
        console.log(`[${safeTitle}] ✓ JSON parsed in ${parseTime}s (total: ${totalTime}s)`);
        console.log(`[RECIPE_DETAILS] [${safeTitle}]`, obj);
        return obj;
      }
    } catch {}

    // Fallback: extract balanced object and repair common issues
    candidate = extractFirstBalancedJsonObject(buffer) || candidate || buffer;
    candidate = repairJson(candidate);

    const obj = JSON.parse(candidate);
    const parseTime = ((Date.now() - parseStartTime) / 1000).toFixed(3);
    const totalTime = ((Date.now() - startTime) / 1000).toFixed(2);
    console.log(`[${safeTitle}] ✓ JSON parsed (fallback) in ${parseTime}s (total: ${totalTime}s)`);
    console.log(`[RECIPE_DETAILS] [${safeTitle}]`, obj);
    return obj;
  } catch (e) {
    // Last resort: try inserting commas at error positions reported by JSON.parse
    try {
      const positionFixed = fixJsonAtErrorPositions(repairJson(buffer));
      if (positionFixed) {
        const parseTime = ((Date.now() - parseStartTime) / 1000).toFixed(3);
        const totalTime = ((Date.now() - startTime) / 1000).toFixed(2);
        console.log(`[${safeTitle}] ✓ JSON parsed (position-fix) in ${parseTime}s (total: ${totalTime}s)`);
        console.log(`[RECIPE_DETAILS] [${safeTitle}]`, positionFixed);
        return positionFixed;
      }
    } catch {}

    // Try to extract partial data from the truncated response
    const partialData = extractPartialRecipeData(buffer);
    if (partialData) {
      const totalTime = ((Date.now() - startTime) / 1000).toFixed(2);
      console.warn(`[${safeTitle}] ⚠️ Recovered partial recipe data after JSON parse repair failed (${totalTime}s):`, Object.keys(partialData), e.message);
      // Return partial data as a valid object instead of raw string
      return {
        ...partialData,
        _partial: true,
        _parseError: e.message
      };
    }

    const totalTime = ((Date.now() - startTime) / 1000).toFixed(2);
    console.error(`[${safeTitle}] ✗ Final JSON parsing failed after ${totalTime}s:`, e);
    // Help debugging: show repaired candidate preview if possible
    try {
      const preview = repairJson(buffer).slice(0, 4000);
      console.debug('Repaired JSON preview (first 4k):', preview);
    } catch {}
    
    // Return minimal fallback object to prevent downstream errors
    console.warn(`[${safeTitle}] ⚠️ Returning minimal fallback object`);
    return {
      full_description: 'Recipe details could not be loaded. Please try again.',
      cooking_instructions: [],
      tips: [],
      equipment: [],
      _partial: true,
      _parseError: e.message
    };
  }
};

/**
 * Fetch alternative recipe suggestions using a lighter/faster model.
 * This is optimized for quick suggestions without full recipe details.
 * 
 * @param {String} prompt - The prompt for generating alternatives
 * @returns {Promise<Object>} Parsed JSON response with alternatives
 */
export const fetchAlternativeRecipes = async (prompt) => {
  const startTime = Date.now();
  console.log(`[Alternatives] 🚀 Fetching alternative recipes...`);
  
  let buffer = '';

  const systemPrompt = `You are a culinary expert. Generate alternative recipe suggestions in valid JSON format only. No markdown, no extra text.`;

  try {
    const alternativeModel = MODEL_CONFIG.alternative;
    const alternativeProvider = getProviderForModel(alternativeModel);
    console.log(`[Alternatives] Using ${alternativeProvider} (${alternativeModel})`);

    const requestBody = {
      model: alternativeModel,
      systemPrompt,
      userPrompt: prompt,
      maxTokens: 2000,
    };

    // Cap reasoning effort — mirrors the recipe-details path above.
    if (alternativeProvider === 'openai' && supportsOpenAIReasoning(alternativeModel)) {
      const reasoningConfig = MODEL_REASONING_CONFIG.alternative || {};
      requestBody.reasoning = { effort: reasoningConfig.effort || 'low' };
      if (reasoningConfig.maxTokens) {
        requestBody.maxTokens = Math.max(requestBody.maxTokens, reasoningConfig.maxTokens);
      }
    }

    await streamFromBackend('/alternatives', requestBody, {
      onText: (deltaText) => { if (deltaText) buffer += deltaText; },
      onFinalText: (text) => { if (text) buffer = text; },
      onError: (err) => console.error('[Alternatives] Stream error:', err),
    });

    const streamTime = ((Date.now() - startTime) / 1000).toFixed(2);
    console.log(`[Alternatives] ✓ Streaming complete after ${streamTime}s`);

    // Parse the response
    let candidate = cleanJsonResponse(buffer);
    try {
      if (candidate && candidate.trim()) {
        const obj = JSON.parse(candidate);
        console.log(`[Alternatives] ✓ Parsed ${obj.alternatives?.length || 0} alternatives`);
        return obj;
      }
    } catch {}

    // Fallback: try to extract and repair JSON
    candidate = extractFirstBalancedJsonObject(buffer) || candidate || buffer;
    candidate = repairJson(candidate);
    try {
      const obj = JSON.parse(candidate);
      console.log(`[Alternatives] ✓ Parsed (fallback) ${obj.alternatives?.length || 0} alternatives`);
      return obj;
    } catch {}

    // Last resort: fix at error positions
    const posFixed = fixJsonAtErrorPositions(candidate);
    if (posFixed) {
      console.log(`[Alternatives] ✓ Parsed (position-fix) ${posFixed.alternatives?.length || 0} alternatives`);
      return posFixed;
    }

    throw new Error('All JSON parsing attempts failed for alternatives');

  } catch (error) {
    console.error(`[Alternatives] ✗ Error:`, error);
    return { alternatives: [] };
  }
};

/**
 * Build the positive prompt from title, ingredients list, and visual description.
 * Format: "{title}", made of {ingredients}.\n{SHOT: ... CAMERA: ... etc.}
 * @param {string} title - Recipe title (updated_title)
 * @param {string} ingredients - Comma-separated ingredient names from the grocery store
 * @param {string|object} visualDescription - visual_description field (SHOT/CAMERA/LIGHTING/...)
 */
const _buildImagePrompt = (title, ingredients, visualDescription) => {
  let visualPrompt = '';

  if (visualDescription && typeof visualDescription === 'string') {
    visualPrompt = visualDescription;
  } else if (visualDescription && typeof visualDescription === 'object') {
    // Check for structured STYLE/SHOT/LIGHTING keys (AI sometimes generates object instead of string)
    const structuredKeys = ['STYLE', 'SHOT', 'LIGHTING', 'SUBJECT', 'PLATING', 'INGREDIENTS', 'GARNISH', 'BACKGROUND'];
    const hasStructuredKeys = structuredKeys.some(k => visualDescription[k] || visualDescription[k.toLowerCase()]);
    if (hasStructuredKeys) {
      const lines = [];
      for (const key of structuredKeys) {
        const val = visualDescription[key] || visualDescription[key.toLowerCase()];
        if (val) lines.push(`${key}: ${val}`);
      }
      visualPrompt = lines.join('\n');
    } else if (visualDescription.full_description) {
      visualPrompt = visualDescription.full_description;
    } else if (Array.isArray(visualDescription.cooking_instructions)) {
      visualPrompt = formatInstructionsForImagePrompt(visualDescription.cooking_instructions);
    } else {
      try { visualPrompt = JSON.stringify(visualDescription).substring(0, 500); } catch {}
    }
  }

  // Build prompt: short ingredient context + visual description
  let positivePrompt = '';
  const safeIngredients = (ingredients || '').trim();
  
  // Include up to 5 key ingredients for context (keeps the model grounded on actual food)
  let ingredientHint = '';
  if (safeIngredients) {
    const ingList = safeIngredients.split(',').map(s => s.trim()).filter(Boolean);
    const keyIngredients = ingList.slice(0, 5).join(', ');
    ingredientHint = `, made of ${keyIngredients}`;
  }
  
  positivePrompt += `A meal${ingredientHint}, with following appearance characteristics:`;
  positivePrompt += '\n';
  
  if (visualPrompt && visualPrompt.trim()) {
    positivePrompt += visualPrompt.trim();
  } else {
    // No visual description available — remove the header too
    positivePrompt = '';
  }

  // Truncate if too long
  if (positivePrompt.length > 1000) {
    positivePrompt = positivePrompt.substring(0, 997) + '...';
  }

  return positivePrompt;
};

//------------------------------------------------
// Concurrency-limited image generation
//------------------------------------------------

/**
 * Internal image generation via backend proxy.
 * Backend handles Runware (primary) → ModelsLab (fallback) with its own retry logic.
 */
const _getRecipeImageInternal = async (title, ingredients, visualDescription = null) => {
  const imageStartTime = Date.now();
  const positivePrompt = _buildImagePrompt(title, ingredients, visualDescription);
  console.log(`[ProgressiveProcessor] 📝 [${title}] positivePrompt (${positivePrompt.length} chars):\n${positivePrompt}`);

  // Guard: if prompt is empty (e.g. parsing failed, no visual_description), skip
  if (!positivePrompt || positivePrompt.trim().length < 2) {
    console.warn(`[${title}] ⚠️ Empty/too-short image prompt — skipping image generation`);
    return null;
  }

  try {
    const result = await fetchFromBackend('/image', { positivePrompt });
    const elapsed = ((Date.now() - imageStartTime) / 1000).toFixed(2);

    if (result?.imageURL) {
      console.log(`[${title}] ✓ Image ready via backend in ${elapsed}s`);
      if (result.cost) {
        generationCostTracker.logImage(title, result.provider || 'backend', result.cost);
      }
      return result.imageURL;
    }

    console.warn(`[${title}] ⚠ Backend returned no image after ${elapsed}s`);
    return null;
  } catch (err) {
    const elapsed = ((Date.now() - imageStartTime) / 1000).toFixed(2);
    console.error(`[${title}] ✗ Image generation failed after ${elapsed}s:`, err.message || err);
    return null;
  }
};

/**
 * Hybrid image scheduling: time-based batching + concurrency semaphore.
 * 
 * Two layers of protection:
 *   1) Time-based: images grouped in batches of 1, with 5s minimum between batches
 *   2) Concurrency: max 3 Runware requests in-flight at any time
 * 
 * This ensures Runware never sees more than 3 simultaneous requests,
 * even when multiple batches overlap. Proven sweet spot: ~3-7s per image.
 */
const IMAGE_BATCH_SIZE = 1;
const IMAGE_BATCH_DELAY_MS = 1000;
const MAX_CONCURRENT_IMAGES = 9;

let _staggerGeneration = 0;
let _batchCounter = 0;
let _batchStartTime = null;

const _imageQueue = {
  running: 0,
  waiters: [],
  async acquire() {
    if (this.running < MAX_CONCURRENT_IMAGES) {
      this.running++;
      return;
    }
    await new Promise(resolve => this.waiters.push(resolve));
    this.running++;
  },
  release() {
    this.running--;
    if (this.waiters.length > 0) {
      const next = this.waiters.shift();
      next();
    }
  },
  reset() {
    this.running = 0;
    const pending = this.waiters.splice(0);
    pending.forEach(resolve => resolve());
  }
};

/**
 * Wait for both: time-based batch slot AND concurrency slot.
 * Time gate runs first (may wait 0-6s), then concurrency gate (may wait if 2 in-flight).
 */
const _waitForImageSlot = async (title) => {
  const mySlot = _batchCounter++;
  const batchIndex = Math.floor(mySlot / IMAGE_BATCH_SIZE);
  const posInBatch = (mySlot % IMAGE_BATCH_SIZE) + 1;

  if (!_batchStartTime) _batchStartTime = Date.now();

  // Layer 1: time-based batch delay
  if (batchIndex > 0) {
    const targetTime = _batchStartTime + (batchIndex * IMAGE_BATCH_DELAY_MS);
    const waitMs = targetTime - Date.now();
    if (waitMs > 0) {
      console.log(`[${title}] 🖼️ Batch #${batchIndex + 1} slot ${posInBatch}/${IMAGE_BATCH_SIZE} — time gate: waiting ${(waitMs / 1000).toFixed(1)}s`);
      await new Promise(r => setTimeout(r, waitMs));
    }
  }

  // Layer 2: concurrency semaphore
  const queueInfo = `running=${_imageQueue.running}/${MAX_CONCURRENT_IMAGES}, waiting=${_imageQueue.waiters.length}`;
  console.log(`[${title}] 🖼️ Batch #${batchIndex + 1} slot ${posInBatch}/${IMAGE_BATCH_SIZE} — acquiring concurrency slot (${queueInfo})`);
  await _imageQueue.acquire();
  console.log(`[${title}] 🚀 Image slot acquired (running=${_imageQueue.running}/${MAX_CONCURRENT_IMAGES})`);
};

/**
 * Reset stagger state between generations.
 * Call when starting a new meal plan to prevent stale slot timestamps.
 */
export const resetImageStagger = () => {
  _staggerGeneration++;
  _batchCounter = 0;
  _batchStartTime = null;
  _imageQueue.reset();
  console.log('[ImageStagger] 🔄 Reset (generation:', _staggerGeneration, ')');
};

/**
 * Generate recipe image: Runware (primary) → ModelsLab (fallback).
 * Called immediately when visual_description becomes available from streaming.
 * Hybrid scheduling: time-based batching (groups of 2, 3s apart) + concurrency limit (max 2).
 * 
 * @param {String} title - Recipe title
 * @param {String} ingredients - Comma-separated ingredient names from the grocery store
 * @param {Object|String} visualDescription - visual_description string or instructions object
 * @returns {Promise<String>} Image URL
 */
export const getRecipeImage = async (title, ingredients, visualDescription = null) => {
  const myGeneration = _staggerGeneration;

  await _waitForImageSlot(title);

  if (myGeneration !== _staggerGeneration) {
    console.log(`[${title}] ⏹️ Image aborted (generation changed ${myGeneration} → ${_staggerGeneration})`);
    _imageQueue.release();
    return null;
  }

  try {
    return await _getRecipeImageInternal(title, ingredients, visualDescription);
  } finally {
    _imageQueue.release();
  }
};

/**
 * Backoff schedule (ms) for `getRecipeImageWithRecovery`. Total worst-case wait ≈ 2.5 min.
 * Each retry is a full `getRecipeImage()` call, which itself does Runware×2 + ModelsLab×1.
 */
export const IMAGE_RECOVERY_BACKOFF_MS = [3000, 10000, 30000, 60000, 60000];

/**
 * Wrapper around `getRecipeImage` that retries on `null` (= all providers failed)
 * with exponential-ish backoff. Stops early if the caller signals abort.
 *
 * @param {string} title
 * @param {string} ingredients - top-N ingredients string
 * @param {Object|string} visualDescription
 * @param {Object} [opts]
 * @param {() => boolean} [opts.isAborted] - returns true to cancel further retries
 * @returns {Promise<string|null>} image URL on success, null after exhausting retries
 */
export const getRecipeImageWithRecovery = async (
  title,
  ingredients,
  visualDescription = null,
  opts = {}
) => {
  const isAborted = typeof opts.isAborted === 'function' ? opts.isAborted : () => false;

  // Initial attempt
  let url = await getRecipeImage(title, ingredients, visualDescription);
  if (url) return url;
  if (isAborted()) {
    console.log(`[ImageRecovery] ⏹️ Aborted before retries for "${title}"`);
    return null;
  }

  for (let i = 0; i < IMAGE_RECOVERY_BACKOFF_MS.length; i++) {
    const delay = IMAGE_RECOVERY_BACKOFF_MS[i];
    console.warn(
      `[ImageRecovery] ↻ Retry ${i + 1}/${IMAGE_RECOVERY_BACKOFF_MS.length} for "${title}" in ${delay}ms`
    );
    // Sleep; bail early on abort
    const sleptOk = await new Promise((resolve) => {
      const t = setTimeout(() => resolve(true), delay);
      // Abort polling — coarse, but fine for this scale
      const poll = setInterval(() => {
        if (isAborted()) {
          clearTimeout(t);
          clearInterval(poll);
          resolve(false);
        }
      }, 500);
      // Also clear poll once main timer fires
      setTimeout(() => clearInterval(poll), delay + 50);
    });
    if (!sleptOk || isAborted()) {
      console.log(`[ImageRecovery] ⏹️ Aborted during backoff for "${title}"`);
      return null;
    }

    url = await getRecipeImage(title, ingredients, visualDescription);
    if (url) {
      console.log(`[ImageRecovery] ✅ Recovered "${title}" on retry ${i + 1}`);
      return url;
    }
    if (isAborted()) {
      console.log(`[ImageRecovery] ⏹️ Aborted after retry ${i + 1} for "${title}"`);
      return null;
    }
  }

  console.warn(`[ImageRecovery] ✗ All retries exhausted for "${title}"`);
  return null;
};

/**
 * Probe image generation backend to check if it's alive.
 * Sends a tiny test request via the backend proxy.
 * Fire-and-forget — does not block the caller.
 */
export const probeRunware = () => {
  console.log('[Image Probe] 🔍 Sending backend health check...');
  fetchFromBackend('/image', { positivePrompt: 'test' })
    .then((result) => {
      if (result?.imageUrl) {
        console.log('[Image Probe] ✅ Backend image generation alive');
      } else {
        console.warn('[Image Probe] ⚠️ Backend returned no image');
      }
    })
    .catch((err) => {
      console.warn('[Image Probe] ❌ Failed:', err.message || err);
    });
};